import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect, Link } from "react-router-dom";
import {
    makeStyles,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { DownloadOutlined, PieChartOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import moment from "moment";
import HttpService from "../../services/httpService";
import { ROLENAME } from "../../utils/index";

const ALLOWED_ROLES = [ROLENAME.OperationDesignManager, ROLENAME.AGM_Design];

const useStyles = makeStyles(() => ({
    pageWrapper: {
        padding: "2rem",
        borderRadius: "10px",
        boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
        backgroundColor: "#fff",
        marginBottom: "1.5rem",
    },
    headerBanner: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "26px 20px",
        borderRadius: "16px",
        marginBottom: "20px",
        background: "linear-gradient(135deg, #1C375D 0%, #2f5583 100%)",
        boxShadow: "0 6px 18px rgba(28,55,93,0.25)",
    },
    title: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        fontWeight: 800,
        color: "#fff",
        letterSpacing: "0.3px",
        fontSize: "28px",
    },
    titleIcon: {
        fontSize: "26px",
        color: "#F47E46",
    },
    subtitle: {
        display: "inline-block",
        marginTop: "12px",
        padding: "5px 14px",
        borderRadius: "20px",
        backgroundColor: "rgba(255,255,255,0.14)",
        color: "rgba(255,255,255,0.92)",
        fontSize: "12.5px",
        fontWeight: 600,
    },
    headerCell: {
        backgroundColor: "#1C375D",
        color: "#fff",
        fontWeight: "bold",
        fontSize: "13px",
        textAlign: "center",
        border: "1px solid rgba(255,255,255,0.2)",
        whiteSpace: "nowrap",
        padding: "10px 14px",
    },
    nameHeaderCell: {
        backgroundColor: "#1C375D",
        color: "#fff",
        fontWeight: "bold",
        fontSize: "13px",
        textAlign: "left",
        border: "1px solid rgba(255,255,255,0.2)",
        whiteSpace: "nowrap",
        padding: "10px 14px",
    },
    nameBodyCell: {
        textAlign: "left",
        fontSize: "13px",
        fontWeight: 600,
        padding: "10px 14px",
        border: "1px solid #e2e8f0",
        color: "#263238",
        backgroundColor: "#fff",
        whiteSpace: "nowrap",
    },
    bodyCell: {
        textAlign: "center",
        fontSize: "13px",
        padding: "10px 14px",
        border: "1px solid #e2e8f0",
        color: "#263238",
        backgroundColor: "#fff",
    },
    clickableValue: {
        fontWeight: 700,
        color: "#1C375D",
        cursor: "pointer",
        textDecoration: "underline",
        "&:hover": { color: "#F47E46" },
    },
    csv: {
        display: "flex",
        justifyContent: "flex-end",
        margin: "16px 0",
    },
    noData: {
        textAlign: "center",
        padding: "40px",
        color: "#94a3b8",
        fontSize: "15px",
    },
    submitButton: {
        backgroundColor: "#F47E46 !important",
        color: "white !important",
        "&:hover": { backgroundColor: "#E0663D !important" },
    },
    clearButton: {
        backgroundColor: "#f7b7b7 !important",
        color: "#151921 !important",
    },
    // Modal table styles
    modalHeaderCell: {
        backgroundColor: "#1C375D",
        color: "#fff",
        fontWeight: "bold",
        fontSize: "13px",
        textAlign: "center",
        borderRight: "1px solid rgba(255,255,255,0.2)",
        borderBottom: "1px solid #fff",
        position: "sticky",
        top: 0,
        zIndex: 2,
        whiteSpace: "nowrap",
        padding: "10px 14px",
    },
    modalBodyCell: {
        textAlign: "center",
        fontSize: "13px",
        padding: "10px 14px",
        border: "1px solid #e2e8f0",
        color: "#263238",
        backgroundColor: "#fff",
    },
    modalRowEven: { backgroundColor: "#f9f9f9" },
    modalRowOdd: { backgroundColor: "#fff" },
    linkStyle: {
        textDecoration: "underline",
        color: "#1C375D",
        fontWeight: 700,
        "&:hover": { color: "#152642" },
    },
}));

const COLS = [
    { key: "activeProjects", label: "Active Projects" },
    { key: "holdProjects", label: "Hold Projects" },
    { key: "totalProjects", label: "Total Projects" },
    { key: "dhsCommitment", label: "DHS Commitment" },
];

const MODAL_COLS_BASE = [
    { label: "Sl. No.", key: "slno" },
    { label: "Customer Name", key: "customerName" },
    { label: "Project No.", key: "erpProjectNo" },
    { label: "Lead No.", key: "lead_no" },
    { label: "Value (INR L)", key: "value" },
    { label: "City", key: "cityName" },
    { label: "Exp. Ctr. Name", key: "experienceCenterName" },
    { label: "Sales Won Date", key: "salesWonDate", isDate: true },
    { label: "Current Stage", key: "currentStage" },
];

const MODAL_COLS_HOLD_EXTRA = [
    { label: "Restoring Project Date", key: "restoringProjectDate", isDate: true },
    { label: "Design Hold Reason", key: "designHoldReason" },
    { label: "Tentative Closure Date", key: "tentativeClosureDate", isDate: true },
    { label: "Remarks", key: "remarks" },
];

const MODAL_COLS_DHS_EXTRA = [
    { label: "Tentative Design Closure Date (Committed Date)", key: "tentativeDesignClosureDate", isDate: true },
    { label: "Projection DSO Committed At", key: "projectionDsoCommittedAt", isDate: true },
    { label: "DSO Month Change", key: "dsoMonthChange", isDate: true },
    { label: "IDSO Date", key: "idsoDate", isDate: true },
];

function getModalCols(type) {
    if (type === "holdProjects") return [...MODAL_COLS_BASE, ...MODAL_COLS_HOLD_EXTRA];
    if (type === "dhsCommitment") return [...MODAL_COLS_BASE, ...MODAL_COLS_DHS_EXTRA];
    return MODAL_COLS_BASE;
}

function normalizeRows(leads = [], cols = MODAL_COLS_BASE) {
    return leads.map((lead, idx) => {
        const row = { ...lead, slno: idx + 1 };
        cols.forEach((col) => {
            if (col.isDate) row[col.key] = row[col.key] ? moment(row[col.key]).format("DD-MM-YYYY") : "-";
        });
        return row;
    });
}

export default function TeamBandwidth() {
    const classes = useStyles();
    const { userDetails } = useSelector((state) => state.user);
    const userRoleName = userDetails?.roles?.[0]?.name || "";
    const isAllowed = ALLOWED_ROLES.includes(userRoleName);
    const isAGM = userRoleName === ROLENAME.AGM_Design;

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [filterOptions, setFilterOptions] = useState({ cities: [], experienceCenters: [] });

    const [filters, setFilters] = useState({
        city: "",
        experienceCenter: "",
        designation: isAGM ? "ODM" : "Designer",
        salesWonStartDate: "",
        salesWonEndDate: "",
    });

    const [modal, setModal] = useState({ open: false, title: "", type: "", leads: [] });

    useEffect(() => {
        fetchTeamBandwidth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchTeamBandwidth = async (appliedFilters = filters) => {
        setLoading(true);
        try {
            const res = await HttpService.getTeamBandwidth(appliedFilters);
            if (res.data?.success) {
                setRows(res.data.rows || []);
                setFilterOptions(res.data.filters || { cities: [], experienceCenters: [] });
            }
        } catch (err) {
            console.error("Error fetching team bandwidth:", err);
        }
        setLoading(false);
    };

    const handleFilterChange = (name, value) => {
        setFilters((prev) => {
            const next = { ...prev, [name]: value };
            if (name === "city") next.experienceCenter = ""; // EC depends on City
            return next;
        });
    };

    const applyFilters = () => fetchTeamBandwidth(filters);

    const clearFilters = () => {
        const def = {
            city: "",
            experienceCenter: "",
            designation: isAGM ? "ODM" : "Designer",
            salesWonStartDate: "",
            salesWonEndDate: "",
        };
        setFilters(def);
        fetchTeamBandwidth(def);
    };

    const availableExperienceCenters = filters.city
        ? filterOptions.experienceCenters.filter((ec) => ec.city === filters.city)
        : filterOptions.experienceCenters;

    const csvHeaders = [
        { label: "Name", key: "name" },
        { label: "Designation", key: "designation" },
        ...COLS.map((c) => ({ label: c.label, key: c.key })),
    ];

    const csvData = rows.map((row) => {
        const flat = { name: row.name, designation: row.designation };
        COLS.forEach((c) => { flat[c.key] = row[c.key]?.count ?? 0; });
        return flat;
    });

    const openModal = (title, leads, type) => setModal({ open: true, title, type, leads: leads || [] });
    const closeModal = () => setModal({ open: false, title: "", type: "", leads: [] });

    const modalCols = getModalCols(modal.type);
    const modalRows = normalizeRows(modal.leads, modalCols);
    const modalCsvHeaders = modalCols.map((c) => ({ label: c.label, key: c.key }));

    if (userDetails?._id && !isAllowed) return <Redirect to="/unauthorized" />;

    return (
        <Paper className={classes.pageWrapper}>
            <div className={classes.headerBanner}>
                <Typography className={classes.title}>
                    <PieChartOutlined className={classes.titleIcon} />
                    Team Bandwidth
                </Typography>
                <Typography className={classes.subtitle}>
                    Live project allocation for your team — click any number to view project details
                </Typography>
            </div>

            <Paper elevation={2} style={{ padding: "16px", marginBottom: "24px" }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl variant="outlined" fullWidth size="small">
                            <InputLabel id="city-label">City</InputLabel>
                            <Select
                                labelId="city-label"
                                label="City"
                                value={filters.city}
                                onChange={(e) => handleFilterChange("city", e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                {filterOptions.cities.map((c) => (
                                    <MenuItem key={c} value={c}>{c}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl variant="outlined" fullWidth size="small">
                            <InputLabel id="ec-label">Experience Centre</InputLabel>
                            <Select
                                labelId="ec-label"
                                label="Experience Centre"
                                value={filters.experienceCenter}
                                onChange={(e) => handleFilterChange("experienceCenter", e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                {availableExperienceCenters.map((ec) => (
                                    <MenuItem key={ec._id} value={ec._id}>{ec.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {isAGM && (
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl variant="outlined" fullWidth size="small">
                                <InputLabel id="designation-label">Designation</InputLabel>
                                <Select
                                    labelId="designation-label"
                                    label="Designation"
                                    value={filters.designation}
                                    onChange={(e) => handleFilterChange("designation", e.target.value)}
                                >
                                    <MenuItem value="All">All</MenuItem>
                                    <MenuItem value="Designer">Designer</MenuItem>
                                    <MenuItem value="ODM">ODM</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Sales Won Start Date"
                            type="date"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={filters.salesWonStartDate}
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) => handleFilterChange("salesWonStartDate", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Sales Won End Date"
                            type="date"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={filters.salesWonEndDate}
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) => handleFilterChange("salesWonEndDate", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <div style={{ display: "flex", gap: "8px" }}>
                            <Button variant="contained" className={classes.submitButton} onClick={applyFilters}>Submit</Button>
                            <Button variant="contained" className={classes.clearButton} onClick={clearFilters}>Clear</Button>
                        </div>
                    </Grid>
                </Grid>
            </Paper>

            {rows.length > 0 && (
                <div className={classes.csv}>
                    <CSVLink data={csvData} headers={csvHeaders} filename={`Team_Bandwidth_${new Date().toISOString().split("T")[0]}.csv`} style={{ textDecoration: "none" }}>
                        <button style={{ display: "flex", alignItems: "center", gap: "6px", backgroundColor: "#355025", color: "#FFF2EB", border: "none", borderRadius: "4px", padding: "7px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                            <DownloadOutlined /> Download CSV
                        </button>
                    </CSVLink>
                </div>
            )}

            {loading ? (
                <Typography className={classes.noData}>Loading...</Typography>
            ) : rows.length === 0 ? (
                <Typography className={classes.noData}>No team members found for the selected filters</Typography>
            ) : (
                <TableContainer>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.nameHeaderCell}>Name</TableCell>
                                {COLS.map((c) => (
                                    <TableCell key={c.key} className={classes.headerCell}>{c.label}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.userId}>
                                    <TableCell className={classes.nameBodyCell}>{row.name}</TableCell>
                                    {COLS.map((c) => {
                                        const metric = row[c.key] || { count: 0, data: [] };
                                        return (
                                            <TableCell key={c.key} className={classes.bodyCell}>
                                                {metric.count > 0 ? (
                                                    <span
                                                        className={classes.clickableValue}
                                                        onClick={() => openModal(`${row.name} — ${c.label}`, metric.data, c.key)}
                                                    >
                                                        {metric.count}
                                                    </span>
                                                ) : (
                                                    metric.count
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* ── Detail Modal ───────────────────────────────────────────────── */}
            <Dialog open={modal.open} onClose={closeModal} maxWidth="xl" fullWidth
                PaperProps={{ style: { borderRadius: "10px", maxHeight: "90vh" } }}>
                <DialogTitle disableTypography style={{ backgroundColor: "#1C375D", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px" }}>
                    <Typography style={{ fontWeight: 700, fontSize: "16px", color: "#fff" }}>
                        {modal.title}
                        <span style={{ marginLeft: 12, fontSize: "13px", fontWeight: 400, opacity: 0.8 }}>
                            ({modalRows.length} records)
                        </span>
                    </Typography>
                    <IconButton size="small" onClick={closeModal} style={{ color: "#fff" }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent style={{ padding: "16px 20px" }}>
                    {modalRows.length > 0 && (
                        <div className={classes.csv}>
                            <CSVLink data={modalRows} headers={modalCsvHeaders}
                                filename={`${modal.title.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.csv`}
                                style={{ textDecoration: "none" }}>
                                <button style={{ display: "flex", alignItems: "center", gap: "6px", backgroundColor: "#355025", color: "#FFF2EB", border: "none", borderRadius: "4px", padding: "7px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                                    <DownloadOutlined /> Download CSV
                                </button>
                            </CSVLink>
                        </div>
                    )}
                    {modalRows.length === 0 ? (
                        <Typography className={classes.noData}>No data found</Typography>
                    ) : (
                        <TableContainer style={{ maxHeight: "65vh", overflowY: "auto" }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {modalCols.map((col) => (
                                            <TableCell key={col.key} className={classes.modalHeaderCell}>{col.label}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {modalRows.map((row, idx) => (
                                        <TableRow key={row.leadId || idx} className={idx % 2 === 0 ? classes.modalRowEven : classes.modalRowOdd}>
                                            {modalCols.map((col) => (
                                                <TableCell key={col.key} className={classes.modalBodyCell}>
                                                    {(col.key === "lead_no" || col.key === "erpProjectNo") && row.leadId ? (
                                                        <Link
                                                            to={`/project-logs/${row.leadId}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={classes.linkStyle}
                                                        >
                                                            {row[col.key]}
                                                        </Link>
                                                    ) : row[col.key]}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>
        </Paper>
    );
}
